import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { IQuestionnaireQuestion } from 'src/app/interfaces/Questionnaire/IQuestionnaire';
import { EpisodeOfCareService } from 'src/app/services/episodeOfCare.service';
import { PermissionService } from 'src/app/services/permission.service';

@Component({
  selector: 'app-gds-questions',
  templateUrl: './gds-questions.component.html',
  styleUrl: './gds-questions.component.css',
  standalone: false,
})
export class GdsQuestionsComponent implements OnInit {
  @Input() elements: IQuestionnaireQuestion[] = [];
  @Input() formGroup!: FormGroup;
  @Input() readonlyMode = false;

  private sub?: Subscription;

  private readonly totalControlName = 'question86';

  //Reverse scored questions: 1,5,6,11,13
  private readonly reverseScoredNames = new Set([
    'question71',
    'question75',
    'question76',
    'question81',
    'question83',
  ]);

  constructor(
    private fb: FormBuilder,
    private readonly eocService: EpisodeOfCareService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly translate: TranslateService,
    private readonly permissionService: PermissionService,
  ) {}

  isInpatientView(): boolean {
    return (
      !(
        this.eocService.currentEocId() === null ||
        this.eocService.currentEocId() === ''
      ) && this.router.url.includes('inPatientView')
    );
  }

  isEOC() {
    return (
      !(
        this.eocService.currentEocId() === null ||
        this.eocService.currentEocId() === ''
      ) && this.router.url.includes('episodeOfCare')
    );
  }

  hasPermission(permission: string[]): boolean {
    return this.permissionService.can(permission);
  }

  ngOnInit() {
    const totalCtrl = this.formGroup.get(this.totalControlName);
    if (totalCtrl) {
      totalCtrl.disable({ emitEvent: false });
    }

    // Recalculate on any changes
    this.sub = this.formGroup.valueChanges.subscribe(() => {
      this.recalculateTotal();
    });

    // Initial
    this.recalculateTotal();
  }

  private recalculateTotal(): void {
    const totalCtrl = this.formGroup.get(this.totalControlName);
    if (!totalCtrl) return;

    let total = 0;

    for (const el of this.elements ?? []) {
      if (!el?.name) continue;
      if (el.name === this.totalControlName) continue;

      // Only GDS scored question types
      if (el.type !== 'radiogroup') continue;

      const selectedValue = this.formGroup.get(el.name)?.value;
      if (selectedValue == null || selectedValue === '') continue;

      // Map selected value ("Item 1"/"Item 2") -> choice text ("Ναι"/"Όχι")
      const choice = (el.choices ?? []).find(
        (c: any) => c.value === selectedValue,
      );
      const selectedText = (choice?.text ?? '').trim();

      const point = this.scoreGdsAnswer(el.name, selectedText);
      total += point;
    }

    totalCtrl.setValue(String(total), { emitEvent: false });
    totalCtrl.disable({ emitEvent: false }); // keep disabled always
    this.cdr.detectChanges();
  }

  private scoreGdsAnswer(questionName: string, selectedText: string): number {
    if (!selectedText) return 0;
    const reverse = this.reverseScoredNames.has(questionName);

    const yes = this.isYes(selectedText);
    const no = this.isNo(selectedText);
    if (!yes && !no) return 0;
    return reverse ? (no ? 1 : 0) : yes ? 1 : 0;
  }

  private norm(s: any): string {
    return String(s ?? '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }
  private isYes(text: string): boolean {
    const t = this.norm(this.translate.instant(text) ?? text);
    return /^(yes|ναι)$/.test(t);
  }

  private isNo(text: string): boolean {
    const t = this.norm(this.translate.instant(text) ?? text);
    return /^(no|οχι)$/.test(t);
  }

  getChoicePoints(questionName: string, choiceText: string): string {
    const reverse = this.reverseScoredNames.has(questionName);

    const yes = this.isYes(choiceText);
    const no = this.isNo(choiceText);
    if (!yes && !no) return '';

    const point = reverse ? (no ? 1 : 0) : yes ? 1 : 0;
    return point === 1 ? '+1' : '0';
  }

  setNotMentioned(fieldName: string): void {
    this.formGroup
      .get(fieldName)
      ?.setValue(this.translate.instant('Not mentioned'));
    this.cdr.detectChanges(); // Ensure UI updates immediately
  }

  onRadioClick(controlName: string, value: any): void {
    if (this.readonlyMode) return;
    const control = this.formGroup.get(controlName);
    if (!control) return;

    if (control.value === value) {
      control.setValue(null); // unselect
    }
  }

  get totalScore(): number {
    const v = this.formGroup?.get(this.totalControlName)?.value;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }

  get gdsCategoryKey(): string {
    const s = this.totalScore;
    if (s <= 5) return 'GDS_NoDepression';
    if (s <= 10) return 'GDS_ModerateDepression';
    return 'GDS_SevereDepression';
  }
}
